import React from 'react';
import styled from 'styled-components';
import {
  FlowCard as Card,
  FlowHeader as Header,
  FlowLink as Link,
  FlowOverall as Overall,
  Fill,
  StepError as ErrorText,
  StepMeta as Meta,
  StepNode as Node,
  StepNodeTop as NodeTop,
  StepPill as Pill,
  StepProgress,
  StepState,
  Track,
  whereOf,
} from './RunStatusUi';
import { RunMetadata, RunTask } from './useRunPipelineMetadata';

type Step = {
  key: string;
  label: string;
  half: string;
  where: string;
  state: StepState;
  percent?: number;
  message?: string;
};

const STAGES: Array<{ key: string; name: string }> = [
  { key: 'detect', name: 'Detect' },
  { key: 'team', name: 'Team model' },
  { key: 'classify', name: 'Teams' },
  { key: 'post', name: 'Post-processing' },
  { key: 'db', name: 'Database' },
];

const stageOf = (name: string) => {
  if (/TC_Train$/.test(name)) return 'team';
  if (/_Classify$/.test(name)) return 'classify';
  if (/Export$/.test(name)) return 'db';
  if (/(PostProcessing|Bridge)$/.test(name)) return 'post';
  return 'detect';
};

const halfOf = (name: string) => (name.startsWith('1Half_') ? '1st half' : name.startsWith('2Half_') ? '2nd half' : 'Both halves');

const labelOf = (name: string) => {
  const rest = name.replace(/^[12]Half_/, '');
  const camera = (side: string) => `${side.toLowerCase()} camera`;
  let m = rest.match(/^Ball_(Left|Right)$/);
  if (m) return `Ball · ${camera(m[1])}`;
  m = rest.match(/^(Left|Right)_Tracking$/);
  if (m) return `Players · ${camera(m[1])}`;
  m = rest.match(/^(Left|Right)_Classify$/);
  if (m) return `Team · ${camera(m[1])}`;
  const known: Record<string, string> = {
    TC_Train: 'Team model',
    Ball_PostProcessing: 'Ball post-processing',
    PostProcessing: 'Players post-processing',
    Ball_Bridge: 'Ball bridge',
    Players_Bridge: 'Players bridge',
    Ball_Export: 'Ball → database',
    Export: 'Players → database',
  };
  return known[rest] || rest.replace(/_/g, ' ');
};

const stateOf = (task: RunTask): StepState => {
  const status = (task.status || '').toUpperCase();
  const life = (task.state || '').toUpperCase();
  if (status === 'SUCCEEDED') return 'done';
  if (status === 'FAILED') return 'failed';
  if (status === 'CANCELLED' || status === 'CANCELED') return 'cancelled';
  if (status === 'RUNNING') return 'running';
  if (life === 'BLOCKED') return 'waiting';
  return 'queued';
};

export const toSteps = (tasks: RunTask[] = []): Step[] =>
  tasks.map((task) => {
    const name = task.name || '';
    return {
      key: name,
      label: labelOf(name),
      half: halfOf(name),
      where: whereOf(task.cluster),
      state: stateOf(task),
      percent: task.percent ?? undefined,
      message: task.stateMessage || undefined,
    };
  });

const Scroll = styled.div`
  overflow-x: auto;
`;

const Columns = styled.div`
  display: grid;
  grid-template-columns: repeat(5, minmax(180px, 1fr));
  gap: 12px;
  min-width: 960px;
`;

const Column = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const ColumnTitle = styled.div`
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #94a3b8;
`;

interface Props {
  runMetadata: RunMetadata;
  percent?: number;
}

const RunStatusFlow: React.FunctionComponent<Props> = ({ runMetadata, percent: jobPercent }) => {
  const steps = toSteps(runMetadata.tasks);
  const done = steps.filter((s) => s.state === 'done').length;
  const running = steps.filter((s) => s.state === 'running').length;
  const percent = jobPercent ?? runMetadata.progress?.percent ?? 0;
  const overallState: StepState = steps.some((s) => s.state === 'failed') ? 'failed' : done === steps.length && steps.length > 0 ? 'done' : 'running';

  return (
    <Card>
      <Header>
        <h2>Pipeline</h2>
        {runMetadata.runPageUrl && (
          <Link href={runMetadata.runPageUrl} target="_blank" rel="noopener noreferrer">
            Details in pipelines
          </Link>
        )}
      </Header>
      <Overall>
        <div>
          <span>
            {done} of {steps.length} steps done{running ? ` · ${running} running` : ''}
          </span>
          <strong>{percent}%</strong>
        </div>
        <Track>
          <Fill percent={percent} state={overallState} />
        </Track>
      </Overall>
      <Scroll>
        <Columns>
          {STAGES.map((stage) => (
            <Column key={stage.key}>
              <ColumnTitle>{stage.name}</ColumnTitle>
              {steps
                .filter((s) => stageOf(s.key) === stage.key)
                .map((s) => (
                  <Node key={s.key} state={s.state}>
                    <NodeTop>
                      <span>{s.label}</span>
                      <Pill state={s.state} />
                    </NodeTop>
                    <Meta>{[s.half, s.where].filter(Boolean).join(' · ')}</Meta>
                    {s.percent != null && s.state !== 'waiting' && (
                      <StepProgress percent={s.percent} state={s.state} />
                    )}
                    {s.state === 'failed' && s.message && <ErrorText>{s.message}</ErrorText>}
                  </Node>
                ))}
            </Column>
          ))}
        </Columns>
      </Scroll>
    </Card>
  );
};

export default RunStatusFlow;
